<template lang="pug">
.index-page
  header-component(v-if="uiLoaded", @reload-data="reloadData", :parentData="headerParentData")

  .sub-header(v-if="uiLoaded")
    .search
      q-input.search-field(
        filled,
        v-model="searchQuery",
        debounce="500",
        :label="searchField.label",
        @input="changeQuery()"
      )
        q-icon.search-field--cancel(name="cancel", v-if="searchQuery", @click.stop="resetSearch()")
    page-filters(
      :filtersData="filters",
      @update-filters="updateFilters",
      :initialFilters="currentFilters",
      @reset-last-filter="resetLastFilter"
    )
    .buttons
      .buttons-item(v-for="button in buttons", :key="button.name", @click="buttonClick(button)")
        q-btn(flat, no-caps) 
          template(v-if="button.icon", slot="default")
            inline-svg.grid-btn-icon-left(:src="require(`../../../assets/icons/header/${button.icon}`)")
          template(v-if="button.label", slot="default")
            span {{ button.label }}

  .content(v-if="content")
    filtered-notification(
      :show-counts="paginationShowCounts",
      :counts="filteredNotificationCounts",
      :style="getFilteredNotificationsStyle"
    )
    table-component(
      v-if="content.type === 'table'",
      ref="tableRef",
      :path="content.data_url",
      :columns="content.columns",
      :query="searchQuery",
      :name="name",
      :additionalFilters="currentFilters",
      @open-show="openShow",
      @open-edit="openEdit",
      @open-issue-form="openIssueForm",
      @show-checklist="openChecklistPreview",
      @update-counts="updateCounts",
      @copy-row-item="copyRowItem",
      @data-loaded="onTableDataLoaded"
    )
  page-dialogs(
    ref="dialogsRef",
    :name="name",
    :reportClass="reportClass",
    :rowId="currentRow",
    @reload="reloadTableData",
    @open-issue-form="openIssueForm",
    @destroy-table-item="destroyTableItem",
    @copy-row-item="copyRowItem"
  )
  .print-pages(v-show="false")
    print-act-page(ref="printActRef", :parentData="{ data: completionCertificate }")
</template>

<script setup>
import { ref, computed, onMounted, nextTick, provide } from "vue";
import headerComponent from "../helpers/Header.vue";
import pageFilters from "./filters/pageFilters.vue";
import tableComponent from "./table/table.vue";
import pageDialogs from "./dialogs/pageDialogs.vue";
import printActPage from "../show_card_components/print/print_completion_certificate.vue";
import FilteredNotification from "../helpers/FilteredNotification.vue";
import backend from "@/api";
import { openTabAndPrintContent } from "@/services/print";
import { handleError } from "@/services/handleErrors";
import printsCss from "../../../assets/styles/prints/index";

const props = defineProps({
  /* Путь для запроса ui страницы */
  path: { type: String, default: "" },
  /** Название текущей страницы. Нужно для вызовов модальных окон страницы. Пример: "acts" */
  name: { type: String, default: "" },
  /** Класс дра репорта текущей страницы. Нужно для export формы */
  reportClass: { type: String, default: () => "" },
});

const emit = defineEmits([
  /** Эмит для назначение loading main компонента */
  "set-loading",
  /** Эмит для открытия формы создания заявки из обращения */
  "open-issue-form",
]);

const uiLoaded = ref(false);
const currentFilters = ref({});
const searchQuery = ref("");
const searchField = ref({});
const buttons = ref([]);
const filters = ref({});
const content = ref(null);
const paginationShowCounts = ref(false);
const filteredNotificationCounts = ref({});
const headerTitle = ref("");
const currentRow = ref(null);
const completionCertificate = ref({});

const dialogsRef = ref(null);
const tableRef = ref(null);
const printActRef = ref(null);
let resolvePendingTableLoad = null;

const headerParentData = computed(() => {
  return {
    data: {
      header: { title: headerTitle.value },
    },
    hide_buttons: true,
    hide_filters: true,
  };
});

const getFilteredNotificationsStyle = computed(() => {
  return {
    marginTop: buttons.length > 0 ? "20px" : "0",
  };
});

const getPageUi = async () => {
  try {
    const { data } = await backend.index(`/api/v3/ui/${props.path}`);

    headerTitle.value = data.title ?? "";
    searchField.value = data.components?.search ?? {};
    buttons.value = data.components?.buttons ?? [];
    filters.value = data.components?.filters ?? {};
    content.value = data.components?.content ?? {};
  } catch (err) {
    await handleError(err, { notify: false });
  } finally {
    uiLoaded.value = true;
    emit("set-loading", false);
  }
};

const updateCounts = ({ counts }) => {
  paginationShowCounts.value = true;
  filteredNotificationCounts.value = counts;
};

const onTableDataLoaded = () => {
  if (resolvePendingTableLoad) {
    resolvePendingTableLoad();
    resolvePendingTableLoad = null;
  }
};

const reloadData = async () => {
  buttons.value = [];
  filters.value = {};
  content.value = {};
  paginationShowCounts.value = false;
  filteredNotificationCounts.value = {};
  uiLoaded.value = false;
  await getPageUi();
};

const updateFilters = filters => {
  currentFilters.value = filters;
  reloadTableData();
};

const reloadTableData = () => {
  tableRef.value.resetData();
};

const resetLastFilter = filters => {
  const filtersKeys = Object.keys(currentFilters.value);

  if (filtersKeys.length > 0) {
    delete currentFilters.value[filtersKeys.pop()];
  }

  // Перезаписываем целиком весь объект чтобы vue увидел глубинные изменения
  currentFilters.value = { ...currentFilters.value };
  tableRef.value.resetData();
};

const resetSearch = () => {
  searchQuery.value = "";
  tableRef.value.resetData();
};

const changeQuery = () => {
  tableRef.value.resetData();
};

const openShow = async id => {
  currentRow.value = id;
  await nextTick();
  dialogsRef.value.openShow();
};

const openEdit = async id => {
  currentRow.value = id;
  await nextTick();
  dialogsRef.value.openEdit();
};

const copyRowItem = async id => {
  try {
    await backend.create(`/api/v3/checklists/${id}/copy`);
    reloadTableData();
  } catch (err) {
    await handleError(err);
  }
};

const openIssueForm = id => {
  emit("open-issue-form", id);
};

const openChecklistPreview = async ({ handler, row }) => {
  dialogsRef.value.openChecklistPreview({ handler, row });
  await nextTick();
};

const buttonClick = button => {
  if (button.type === "create") {
    dialogsRef.value.openCreate();
  } else if (button.type === "export") {
    dialogsRef.value.openExport();
  }
};

const resetCurrentRow = () => {
  currentRow.value = null;
};

const openPrintAct = async printData => {
  completionCertificate.value = printData;
  await nextTick();

  openTabAndPrintContent({
    html: printActRef.value.$el.innerHTML,
    css: printsCss.completion_certificate,
    closeModalFunction: undefined,
    popup: true,
  });
};

const updateRow = id => {
  tableRef.value.updateRow(id);
};

const setSearchQuery = async value => {
  searchQuery.value = value == null ? "" : String(value);

  if (!tableRef.value) {
    await new Promise(resolve => {
      resolvePendingTableLoad = resolve;
    });
    return;
  }

  await nextTick();
  await tableRef.value?.resetData();
};

const destroyTableItem = id => {
  tableRef.value.destroyTableItem(id);
};

const openCustomCreate = options => {
  dialogsRef.value.openCustomCreate(options);
};

provide("openPrintAct", openPrintAct);
provide("updateRow", updateRow);

defineExpose({ openShow, setSearchQuery, reloadTableData, openCustomCreate });

onMounted(async () => {
  await getPageUi();
});
</script>

<style lang="scss" scoped>
.index-page {
  .sub-header {
    display: flex;
    gap: 16px;

    > div {
      width: fit-content;
    }

    .search {
      &-field {
        width: 562px;
        float: left;
        border-radius: 5px;

        :deep(.q-field__inner) {
          height: 45px !important;

          .q-field__control {
            height: 45px !important;
            border-radius: 8px;
            background-color: var(--field-background);
            border: var(--field-border);

            .q-field__label {
              color: var(--field-label-color);
              margin-top: -5px;
              font-size: 15px;
            }

            input {
              color: var(--field-input-color);
              margin-top: -5px;
            }
          }
        }

        &--cancel {
          position: absolute;
          right: 5px;
          margin-top: 16px;
          cursor: pointer;
          color: var(--field-cancel-icon-color);
        }
      }
    }

    .filters {
      display: flex;
      align-items: center;
      gap: 10px;
      cursor: pointer;
      text-decoration: underline;
      color: #5b7aff;
      font-weight: 600;
    }

    .buttons {
      margin-left: auto;
      display: flex;
      justify-content: space-between;
      align-items: center;

      &-item {
        button {
          padding-left: 15px;
          padding-right: 15px;
          margin-left: 10px;
          border-radius: 8px;
          background-color: #5b7aff;
          color: #fff;

          span {
            font-style: normal;
            font-weight: bold;
            line-height: 20px;
          }
        }
      }
    }
  }
}
</style>
